import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import axios from 'axios'
import Cookies from 'js-cookie'
import API_BASE_URL from '../../../config/api'
import Topbar from '../mini-comp/Topbar'
import Sidebar from '../mini-comp/Sidebar'
import Swal from 'sweetalert2'
import {
    RiFingerprintLine,
    RiCalendarLine,
    RiMailLine,
    RiPhoneLine,
    RiCake2Line,
    RiUserLine,
    RiMapPinLine,
    RiHeart2Line,
    RiTeamLine,
    RiShieldUserLine,
    RiBriefcaseLine,
    RiGroupLine,
    RiUserHeartLine,
    RiDeleteBinLine,
    RiEditLine,
    RiEdit2Line,
    RiSubtractLine,
    RiFileTextLine,
    RiDownloadLine,
    RiAlertLine,
    RiClockwise2Line,
    RiNumbersLine,
    RiBellLine,
    RiNumber8
} from 'react-icons/ri'
import { FaClock } from 'react-icons/fa'
import { Fa6 } from 'react-icons/fa6'

const EmployeeDetails = () => {
    const { id } = useParams();
    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submittingChild, setSubmittingChild] = useState(false);
    const [isEditingChild, setIsEditingChild] = useState(false);
    const [editingChildId, setEditingChildId] = useState(null);
    const [childForm, setChildForm] = useState({
        name: '',
        gender: '',
        age: ''
    });

    const [submittingQual, setSubmittingQual] = useState(false);
    const [isEditingQual, setIsEditingQual] = useState(false);
    const [editingQualId, setEditingQualId] = useState(null);
    const [qualForm, setQualForm] = useState({
        qualification_type: '',
        name: '',
        institution: '',
        year_completed: ''
    });

    const [submittingContract, setSubmittingContract] = useState(false);
    const [isEditingContract, setIsEditingContract] = useState(false);
    const [editingContractId, setEditingContractId] = useState(null);
    const [contractForm, setContractForm] = useState({
        contract_type: '',
        start_date: '',
        end_date: '',
        salary: '',
        probation_period: 0,
        probation_end_date: '',
    });

    const [submittingDocument, setSubmittingDocument] = useState(false);
    const [isEditingDocument, setIsEditingDocument] = useState(false);
    const [editingDocumentId, setEditingDocumentId] = useState(null);
    const [documentForm, setDocumentForm] = useState({
        document_type: '',
        document_file: null
    });

    const [submittingRecord, setSubmittingRecord] = useState(false);
    const [isEditingRecord, setIsEditingRecord] = useState(false);
    const [editingRecordId, setEditingRecordId] = useState(null);
    const [recordForm, setRecordForm] = useState({
        record_type: '',
        description: '',
        record_file: null
    });

    const fetchEmployeeData = async () => {
        try {
            setLoading(true);
            const token = Cookies.get('token');
            const response = await axios.get(`${API_BASE_URL}/api/v1/employees/${id}/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setEmployee(response.data);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching employee data:", err);
            setError(err.response?.data?.message || err.message || "Failed to fetch employee details");
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchEmployeeData();
        }
    }, [id]);

    const handleChildInputChange = (e) => {
        const { name, value } = e.target;
        setChildForm(prev => ({ ...prev, [name]: value }));
    };

    const handleEditChild = (child) => {
        setChildForm({
            name: child.name,
            gender: child.gender,
            age: child.age
        });
        setEditingChildId(child.id);
        setIsEditingChild(true);
        // Open modal manually if needed, but data-bs-toggle should work if we trigger it correctly
    };

    const handleAddChild = async (e) => {
        e.preventDefault();

        if (parseInt(childForm.age) >= 18) {
            Swal.fire('Error', 'Child age must be below 18.', 'error');
            return;
        }

        setSubmittingChild(true);
        try {
            const token = Cookies.get('token');
            if (isEditingChild) {
                await axios.put(`${API_BASE_URL}/api/v1/employees/children-detail/${editingChildId}/`, childForm, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                Swal.fire('Success', 'Child updated successfully!', 'success');
            } else {
                await axios.post(`${API_BASE_URL}/api/v1/employees/children/${id}/`, childForm, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                Swal.fire('Success', 'Child added successfully!', 'success');
            }

            setChildForm({ name: '', gender: '', age: '' });
            setIsEditingChild(false);
            setEditingChildId(null);

            // Close modal
            const closeBtn = document.querySelector('#addChildModal .btn-close');
            if (closeBtn) closeBtn.click();

            fetchEmployeeData();
        } catch (err) {
            console.error("Error saving child:", err);
            Swal.fire('Error', err.response?.data?.error || 'Failed to save child.', 'error');
        } finally {
            setSubmittingChild(false);
        }
    };

    const handleDeleteChild = async (childId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "Remove this child record?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const token = Cookies.get('token');
                await axios.delete(`${API_BASE_URL}/api/v1/employees/children-detail/${childId}/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                Swal.fire('Deleted!', 'Record removed.', 'success');
                fetchEmployeeData();
            } catch (err) {
                Swal.fire('Error', 'Failed to delete record.', 'error');
            }
        }
    };

    const handleQualInputChange = (e) => {
        const { name, value } = e.target;
        setQualForm(prev => ({ ...prev, [name]: value }));
    };

    const handleEditQual = (qual) => {
        setQualForm({
            qualification_type: qual.qualification_type,
            name: qual.name,
            institution: qual.institution,
            year_completed: qual.year_completed
        });
        setEditingQualId(qual.id);
        setIsEditingQual(true);
    };

    const handleAddQual = async (e) => {
        e.preventDefault();
        setSubmittingQual(true);
        try {
            const token = Cookies.get('token');
            if (isEditingQual) {
                await axios.put(`${API_BASE_URL}/api/v1/employees/qualifications-detail/${editingQualId}/`, qualForm, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                Swal.fire('Success', 'Qualification updated successfully!', 'success');
            } else {
                await axios.post(`${API_BASE_URL}/api/v1/employees/qualifications/${id}/`, qualForm, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                Swal.fire('Success', 'Qualification added successfully!', 'success');
            }

            setQualForm({ qualification_type: '', name: '', institution: '', year_completed: '' });
            setIsEditingQual(false);
            setEditingQualId(null);

            // Close modal
            const closeBtn = document.querySelector('#addQualModal .btn-close');
            if (closeBtn) closeBtn.click();

            fetchEmployeeData();
        } catch (err) {
            console.error("Error saving qualification:", err);
            Swal.fire('Error', err.response?.data?.error || 'Failed to save qualification.', 'error');
        } finally {
            setSubmittingQual(false);
        }
    };

    const handleDeleteQual = async (qualId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "Remove this qualification record?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const token = Cookies.get('token');
                await axios.delete(`${API_BASE_URL}/api/v1/employees/qualification-detail/${qualId}/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                Swal.fire('Deleted!', 'Qualification removed.', 'success');
                fetchEmployeeData();
            } catch (err) {
                Swal.fire('Error', 'Failed to delete qualification.', 'error');
            }
        }
    };

    const handleContractInputChange = (e) => {
        const { name, value } = e.target;
        setContractForm(prev => {
            const updated = { ...prev, [name]: value };

            // Auto-calculate probation end date
            if (updated.contract_type === 'fixed_term' && updated.start_date) {
                const months = parseInt(updated.probation_period);
                if (!isNaN(months) && months > 0) {
                    const startDate = new Date(updated.start_date);
                    startDate.setMonth(startDate.getMonth() + months);
                    updated.probation_end_date = startDate.toISOString().split('T')[0];
                } else {
                    updated.probation_end_date = '';
                }
            } else if (updated.contract_type !== 'fixed_term') {
                updated.probation_period = 0;
                updated.probation_end_date = '';
            }

            return updated;
        });
    };

    const handleEditContract = (contract) => {
        setContractForm({
            contract_type: contract.contract_type,
            start_date: contract.start_date,
            end_date: contract.end_date,
            salary: contract.salary,
            probation_period: contract.probation_period,
            probation_end_date: contract.probation_end_date
        });
        setEditingContractId(contract.id);
        setIsEditingContract(true);
    };

    const handleAddContract = async (e) => {
        e.preventDefault();
        setSubmittingContract(true);
        try {
            const token = Cookies.get('token');
            const payload = {
                ...contractForm,
                probation_period: parseInt(contractForm.probation_period) || 0,
                salary: parseFloat(contractForm.salary) || 0,
                end_date: contractForm.end_date || null,
                probation_end_date: contractForm.probation_end_date || null
            };

            if (isEditingContract) {
                await axios.put(`${API_BASE_URL}/api/v1/employees/contract-detail/${editingContractId}/`, payload, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                Swal.fire('Success', 'Contract updated successfully!', 'success');
            } else {
                await axios.post(`${API_BASE_URL}/api/v1/employees/contracts/${id}/`, payload, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                Swal.fire('Success', 'Contract added successfully!', 'success');
            }

            setContractForm({ contract_type: '', start_date: '', end_date: '', salary: '', probation_period: 0, probation_end_date: '' });
            setIsEditingContract(false);
            setEditingContractId(null);

            // Close modal
            const closeBtn = document.querySelector('#addContractModal .btn-close');
            if (closeBtn) closeBtn.click();

            fetchEmployeeData();
        } catch (err) {
            console.error("Error saving contract:", err);
            Swal.fire('Error', err.response?.data?.error || 'Failed to save contract.', 'error');
        } finally {
            setSubmittingContract(false);
        }
    };

    const handleTerminateContract = async (contractId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "Terminate this contract?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, terminate it!'
        });

        if (result.isConfirmed) {
            try {
                const token = Cookies.get('token');
                await axios.patch(`${API_BASE_URL}/api/v1/employees/contract-detail/${contractId}/`, { is_active: false }, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                Swal.fire('Terminated!', 'Contract terminated.', 'success');
                fetchEmployeeData();
            } catch (err) {
                Swal.fire('Error', 'Failed to terminate contract.', 'error');
            }
        }
    };

    const handleDocumentInputChange = (e) => {
        const { name, value, files } = e.target;
        if (name === 'document_file') {
            setDocumentForm(prev => ({ ...prev, document_file: files[0] }));
        } else {
            setDocumentForm(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleEditDocument = (doc) => {
        setDocumentForm({
            document_type: doc.document_type,
            document_file: null
        });
        setEditingDocumentId(doc.id);
        setIsEditingDocument(true);
    };

    const handleAddDocument = async (e) => {
        e.preventDefault();
        setSubmittingDocument(true);
        try {
            const token = Cookies.get('token');
            const formData = new FormData();
            formData.append('document_type', documentForm.document_type);
            if (documentForm.document_file) {
                formData.append('document_file', documentForm.document_file);
            }

            if (isEditingDocument) {
                await axios.put(`${API_BASE_URL}/api/v1/employees/documents-detail/${editingDocumentId}/`, formData, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
                Swal.fire('Success', 'Document updated successfully!', 'success');
            } else {
                await axios.post(`${API_BASE_URL}/api/v1/employees/documents/${id}/`, formData, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
                Swal.fire('Success', 'Document added successfully!', 'success');
            }

            setDocumentForm({ document_type: '', document_file: null });
            setIsEditingDocument(false);
            setEditingDocumentId(null);

            // Close modal
            const closeBtn = document.querySelector('#addDocumentModal .btn-close');
            if (closeBtn) closeBtn.click();

            fetchEmployeeData();
        } catch (err) {
            console.error("Error saving document:", err);
            Swal.fire('Error', err.response?.data?.error || 'Failed to save document.', 'error');
        } finally {
            setSubmittingDocument(false);
        }
    };

    const handleDeleteDocument = async (docId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "Remove this document?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const token = Cookies.get('token');
                await axios.delete(`${API_BASE_URL}/api/v1/employees/documents-detail/${docId}/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                Swal.fire('Deleted!', 'Document removed.', 'success');
                fetchEmployeeData();
            } catch (err) {
                Swal.fire('Error', 'Failed to delete document.', 'error');
            }
        }
    };

    const handleRecordInputChange = (e) => {
        const { name, value, files } = e.target;
        if (name === 'record_file') {
            setRecordForm(prev => ({ ...prev, record_file: files[0] }));
        } else {
            setRecordForm(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleEditRecord = (record) => {
        setRecordForm({
            record_type: record.record_type,
            description: record.description,
            record_file: null
        });
        setEditingRecordId(record.id);
        setIsEditingRecord(true);
    };

    const handleAddRecord = async (e) => {
        e.preventDefault();
        setSubmittingRecord(true);
        try {
            const token = Cookies.get('token');
            const formData = new FormData();
            formData.append('record_type', recordForm.record_type);
            formData.append('description', recordForm.description);
            if (recordForm.record_file) {
                formData.append('record_file', recordForm.record_file);
            }

            if (isEditingRecord) {
                await axios.put(`${API_BASE_URL}/api/v1/employees/disciplinary-records-detail/${editingRecordId}/`, formData, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
                Swal.fire('Success', 'Disciplinary record updated successfully!', 'success');
            } else {
                await axios.post(`${API_BASE_URL}/api/v1/employees/disciplinary-records/${id}/`, formData, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
                Swal.fire('Success', 'Disciplinary record added successfully!', 'success');
            }

            setRecordForm({ record_type: '', description: '', record_file: null });
            setIsEditingRecord(false);
            setEditingRecordId(null);

            // Close modal
            const closeBtn = document.querySelector('#addRecordModal .btn-close');
            if (closeBtn) closeBtn.click();

            fetchEmployeeData();
        } catch (err) {
            console.error("Error saving disciplinary record:", err);
            Swal.fire('Error', err.response?.data?.error || 'Failed to save disciplinary record.', 'error');
        } finally {
            setSubmittingRecord(false);
        }
    };

    const handleDeleteRecord = async (recordId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "Remove this disciplinary record?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const token = Cookies.get('token');
                await axios.delete(`${API_BASE_URL}/api/v1/employees/disciplinary-records-detail/${recordId}/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                Swal.fire('Deleted!', 'Disciplinary record removed.', 'success');
                fetchEmployeeData();
            } catch (err) {
                Swal.fire('Error', 'Failed to delete disciplinary record.', 'error');
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getGenderLabel = (gender) => {
        const labels = {
            'M': 'Male',
            'F': 'Female'
        };
        return labels[gender] || gender || 'N/A';
    };

    const getStatusBadge = (status) => {
        const badges = {
            active: 'bg-label-success',
            terminated: 'bg-label-danger',
            on_leave: 'bg-label-warning',
            suspended: 'bg-label-info'
        };
        return badges[status?.toLowerCase()] || 'bg-label-secondary';
    };

    if (loading) {
        return (
            <div>
                <Topbar />
                <Sidebar />
                <div className="app-content-area">
                    <div className="app-content-wrap">
                        <div className="container-fluid">
                            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div>
                <Topbar />
                <Sidebar />
                <div className="app-content-area">
                    <div className="app-content-wrap">
                        <div className="container-fluid">
                            <div className="alert alert-danger m-4" role="alert">
                                <h4 className="alert-heading">Error!</h4>
                                <p>{error}</p>
                                <hr />
                                <button className="btn btn-primary" onClick={() => window.location.reload()}>Retry</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!employee) return null;

    return (
        <div>
            <Topbar />
            <Sidebar />

            <>{ /* app-content-area-start */}
                <div className="app-content-area">
                    <div className="app-content-wrap">
                        <div className="container-fluid">
                            <div className="row">
                                <div className="col-xxl-4 col-xl-12">
                                    <div className="card height-equal">
                                        <div className="card-header d-flex justify-content-between align-items-center">
                                            <h4 className="mb-0">Personal Information</h4>
                                            <Link to={`/hr/edit-employee/${id}`} className="btn btn-primary btn-sm d-flex align-items-center gap-1">
                                                <RiEditLine /> Edit Employee
                                            </Link>
                                        </div>
                                        <div className="card-body pt-15">
                                            <div className="text-center mb-10">
                                                <div className="avatar avatar-big radius-100 shadow-sm border p-1 bg-white">
                                                    <img
                                                        className="radius-100 object-fit-cover"
                                                        src={employee.photo ? (employee.photo.startsWith('http') ? employee.photo : API_BASE_URL + employee.photo) : "/assets/images/avatar/avatar-thumb-001.webp"}
                                                        alt={employee.full_name}
                                                        style={{ width: '120px', height: '120px' }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="profile-info text-center mb-25">
                                                <div className="d-flex align-items-center justify-content-center gap-10 mb-10">
                                                    <h3 className="mb-0 fw-bold">{employee.full_name}</h3>
                                                    <span className={`badge ${getStatusBadge(employee.employment_status)} border-radius-5 fs-11 px-3`}>
                                                        {employee.employment_status?.toUpperCase()}
                                                    </span>
                                                </div>
                                                <h6 className="text-muted mb-0 fw-6 fs-15">{employee.position}</h6>
                                            </div>

                                            <div className="employee-details-content">
                                                {/* General Information Group */}
                                                <div className="info-section mb-25">
                                                    <div className="section-header d-flex align-items-center gap-10 mb-15 border-bottom pb-2">
                                                        <RiShieldUserLine className="text-primary fs-20" />
                                                        <h6 className="mb-0 text-uppercase tracking-wider fs-13 fw-bold">General Information</h6>
                                                    </div>
                                                    <div className="row g-4">
                                                        <div className="col-md-4 col-sm-6">
                                                            <div className="detail-item">
                                                                <span className="text-muted fs-12 d-block mb-1">Employee ID</span>
                                                                <div className="d-flex align-items-center gap-8">
                                                                    <RiFingerprintLine className="text-primary" />
                                                                    <span className="fw-bold">{employee.employee_id}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4 col-sm-6">
                                                            <div className="detail-item">
                                                                <span className="text-muted fs-12 d-block mb-1">Category</span>
                                                                <div className="d-flex align-items-center gap-8">
                                                                    <RiBriefcaseLine className="text-primary" />
                                                                    <span className="fw-bold text-capitalize">{employee.category}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4 col-sm-6">
                                                            <div className="detail-item">
                                                                <span className="text-muted fs-12 d-block mb-1">Date of Join</span>
                                                                <div className="d-flex align-items-center gap-8">
                                                                    <RiCalendarLine className="text-primary" />
                                                                    <span className="fw-bold">{formatDate(employee.created_at)}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4 col-sm-6">
                                                            <div className="detail-item">
                                                                <span className="text-muted fs-12 d-block mb-1">Gender</span>
                                                                <div className="d-flex align-items-center gap-8">
                                                                    <RiUserLine className="text-primary" />
                                                                    <span className="fw-bold">{getGenderLabel(employee.gender)}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4 col-sm-6">
                                                            <div className="detail-item">
                                                                <span className="text-muted fs-12 d-block mb-1">Birthday</span>
                                                                <div className="d-flex align-items-center gap-8">
                                                                    <RiCake2Line className="text-primary" />
                                                                    <span className="fw-bold">{formatDate(employee.date_of_birth)}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4 col-sm-6">
                                                            <div className="detail-item">
                                                                <span className="text-muted fs-12 d-block mb-1">Marital Status</span>
                                                                <div className="d-flex align-items-center gap-8">
                                                                    <RiHeart2Line className="text-primary" />
                                                                    <span className="fw-bold text-capitalize">{employee.marital_status}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4 col-sm-6">
                                                            <div className="detail-item">
                                                                <span className="text-muted fs-12 d-block mb-1">Working Days/Month</span>
                                                                <div className="d-flex align-items-center gap-8">
                                                                    <RiCalendarLine className="text-primary" />
                                                                    <span className="fw-bold text-capitalize">{employee.working_days_per_month}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4 col-sm-6">
                                                            <div className="detail-item">
                                                                <span className="text-muted fs-12 d-block mb-1">Working Hours/Day</span>
                                                                <div className="d-flex align-items-center gap-12">
                                                                    <FaClock className="text-primary" />
                                                                    <span className="fw-bold text-capitalize">{employee.working_hours_per_day}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4 col-sm-6">
                                                            <div className="detail-item">
                                                                <span className="text-muted fs-12 d-block mb-1">Hourly Rate</span>
                                                                <div className="d-flex align-items-center gap-12">
                                                                    <FaClock className="text-primary" />
                                                                    <span className="fw-bold text-capitalize">{employee.hourly_rate}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4 col-sm-6">
                                                            <div className="detail-item">
                                                                <span className="text-muted fs-12 d-block mb-1">SSR Number</span>
                                                                <div className="d-flex align-items-center gap-8">
                                                                    <span className="fw-bold text-capitalize">{employee.nssa_ssr_number}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4 col-sm-6">
                                                            <div className="detail-item">
                                                                <span className="text-muted fs-12 d-block mb-1">TIN NUmber</span>
                                                                <div className="d-flex align-items-center gap-8">
                                                                    <span className="fw-bold text-capitalize">{employee.zimra_tin_number}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Contact Information Group */}
                                                <div className="info-section mb-25">
                                                    <div className="section-header d-flex align-items-center gap-10 mb-15 border-bottom pb-2">
                                                        <RiMailLine className="text-primary fs-20" />
                                                        <h6 className="mb-0 text-uppercase tracking-wider fs-13 fw-bold">Contact Details</h6>
                                                    </div>
                                                    <div className="row g-4">
                                                        <div className="col-md-6">
                                                            <div className="detail-item p-3 bg-light-subtle radius-10">
                                                                <span className="text-muted fs-12 d-block mb-1">Email Address</span>
                                                                <div className="d-flex align-items-center gap-8">
                                                                    <RiMailLine className="text-primary text-opacity-50" />
                                                                    <span className="fw-bold">{employee.email}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <div className="detail-item p-3 bg-light-subtle radius-10">
                                                                <span className="text-muted fs-12 d-block mb-1">Phone Number</span>
                                                                <div className="d-flex align-items-center gap-8">
                                                                    <RiPhoneLine className="text-primary text-opacity-50" />
                                                                    <span className="fw-bold">{employee.phone_number}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-12">
                                                            <div className="detail-item p-3 bg-light-subtle radius-10 border-start-primary">
                                                                <span className="text-muted fs-12 d-block mb-1">Home Address</span>
                                                                <div className="d-flex align-items-start gap-8">
                                                                    <RiMapPinLine className="text-primary mt-1" />
                                                                    <span className="fw-6 fs-14">{employee.home_address}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Spouse & Family Group (Conditional) */}
                                                {(employee.marital_status?.toLowerCase() === 'married' || employee.marital_status === 'Married') && (
                                                    <div className="info-section mb-25">
                                                        <div className="section-header d-flex align-items-center gap-10 mb-15 border-bottom pb-2">
                                                            <RiTeamLine className="text-primary fs-20" />
                                                            <h6 className="mb-0 text-uppercase tracking-wider fs-13 fw-bold">Family Details</h6>
                                                        </div>
                                                        <div className="row g-4">
                                                            <div className="col-md-6">
                                                                <div className="detail-item">
                                                                    <span className="text-muted fs-12 d-block mb-1">Spouse Name</span>
                                                                    <span className="fw-bold">{employee.spouse_name}</span>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-6">
                                                                <div className="detail-item">
                                                                    <span className="text-muted fs-12 d-block mb-1">Spouse Contact</span>
                                                                    <span className="fw-bold">{employee.spouse_contact}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Next of Kin Group (Conditional: Hide if Married) */}
                                                {(employee.marital_status?.toLowerCase() !== 'married' && employee.marital_status !== 'Married') && (
                                                    <div className="info-section">
                                                        <div className="section-header d-flex align-items-center gap-10 mb-15 border-bottom pb-2">
                                                            <RiGroupLine className="text-primary fs-20" />
                                                            <h6 className="mb-0 text-uppercase tracking-wider fs-13 fw-bold">Next of Kin / Emergency</h6>
                                                        </div>
                                                        <div className="row g-4">
                                                            <div className="col-md-4">
                                                                <div className="detail-item">
                                                                    <span className="text-muted fs-12 d-block mb-1">Full Name</span>
                                                                    <span className="fw-bold">{employee.next_of_kin_name}</span>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-4">
                                                                <div className="detail-item">
                                                                    <span className="text-muted fs-12 d-block mb-1">Relationship</span>
                                                                    <span className="fw-bold">{employee.next_of_kin_relationship}</span>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-4">
                                                                <div className="detail-item">
                                                                    <span className="text-muted fs-12 d-block mb-1">Contact</span>
                                                                    <span className="fw-bold">{employee.next_of_kin_contact}</span>
                                                                </div>
                                                            </div>
                                                            <div className="col-12">
                                                                <div className="detail-item">
                                                                    <span className="text-muted fs-12 d-block mb-1">Emergency Address</span>
                                                                    <span className="fw-6 fs-14">{employee.next_of_kin_address}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xxl-4 col-xl-6 col-lg-6">
                                    <div className="card height-equal">
                                        <div className="card-header justify-between">
                                            <h4 className="">Children</h4>
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm"
                                                data-bs-toggle="modal"
                                                data-bs-target="#addChildModal"
                                                onClick={() => {
                                                    setIsEditingChild(false);
                                                    setEditingChildId(null);
                                                    setChildForm({ name: '', gender: '', age: '' });
                                                }}
                                            >
                                                Add Child
                                            </button>
                                        </div>
                                        <div className="card-body pt-15">
                                            {employee.children && employee.children.length > 0 ? (
                                                employee.children.map((child, index) => (
                                                    <div className="exp-timeline-item mb-25" key={child.id || index}>
                                                        <div className="exp-timeline-badge btn-icon btn-sm btn-info-light fs-20">
                                                            <RiUserHeartLine />
                                                        </div>
                                                        <div className="exp-timeline-content">
                                                            <div className="exp-timeline-header d-flex-between gap-15 mb-5">
                                                                <h5>{child.name}</h5>
                                                                <div className="d-flex gap-2">
                                                                    <button
                                                                        className="btn btn-sm btn-info-light btn-icon"
                                                                        data-bs-toggle="modal"
                                                                        data-bs-target="#addChildModal"
                                                                        onClick={() => handleEditChild(child)}
                                                                    >
                                                                        <RiEditLine />
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm btn-danger-light btn-icon"
                                                                        onClick={() => handleDeleteChild(child.id)}
                                                                    >
                                                                        <RiDeleteBinLine />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            <div className="d-flex gap-20">
                                                                <span className="fs-14 text-muted">Gender: <span className="fw-6 text-dark text-capitalize">{child.gender}</span></span>
                                                                <span className="fs-14 text-muted">Age: <span className="fw-6 text-dark">{child.age} Yrs</span></span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="text-center py-4">
                                                    <RiUserHeartLine size={40} className="text-muted mb-10 opacity-25" />
                                                    <p className="text-muted mb-0">No children records found.</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xxl-4 col-xl-6 col-lg-6">
                                    <div className="card height-equal">
                                        <div className="card-header d-flex justify-content-between align-items-center">
                                            <h4 className="mb-0">Education Qualifications</h4>
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm"
                                                data-bs-toggle="modal"
                                                data-bs-target="#addQualModal"
                                                onClick={() => {
                                                    setIsEditingQual(false);
                                                    setEditingQualId(null);
                                                    setQualForm({ qualification_type: '', name: '', institution: '', year_completed: '' });
                                                }}
                                            >
                                                Add Qualification
                                            </button>
                                        </div>
                                        <div className="card-body pt-15">
                                            {employee.qualifications && employee.qualifications.length > 0 ? (
                                                employee.qualifications.map((qual, index) => (
                                                    <div className="edu-timeline-item mb-25" key={qual.id || index}>
                                                        <div className="edu-timeline-content">
                                                            <div className="edu-timeline-header d-flex-between gap-15 mb-5">
                                                                <h5>{qual.name} ({qual.qualification_type})</h5>
                                                                <div className="d-flex gap-2 align-items-center">
                                                                    <span className="edu-timeline-date me-2">{qual.year_completed || 'N/A'}</span>
                                                                    <button
                                                                        className="btn btn-sm btn-info-light btn-icon"
                                                                        data-bs-toggle="modal"
                                                                        data-bs-target="#addQualModal"
                                                                        onClick={() => handleEditQual(qual)}
                                                                    >
                                                                        <RiEditLine />
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm btn-danger-light btn-icon"
                                                                        onClick={() => handleDeleteQual(qual.id)}
                                                                    >
                                                                        <RiDeleteBinLine />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            <span className="institution-name d-block fs-15 fw-6 mb-5">{qual.institution || 'N/A'}</span>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <p className="text-muted text-center py-4">No qualifications listed.</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xxl-8 col-xl-12">
                                    <div className="card">
                                        <div className="card-header d-flex justify-content-between align-items-center">
                                            <h4 className="mb-0">Contracts</h4>
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm"
                                                data-bs-toggle="modal"
                                                data-bs-target="#addContractModal"
                                                onClick={() => {
                                                    setIsEditingContract(false);
                                                    setEditingContractId(null);
                                                    setContractForm({ contract_type: '', start_date: '', end_date: '', salary: '', probation_period: 0, probation_end_date: '' });
                                                }}
                                            >
                                                Add Contract
                                            </button>
                                        </div>
                                        <div className="card-body pt-15">
                                            <div className="table-responsive">
                                                <table className="table text-nowrap">
                                                    <thead>
                                                        <tr>
                                                            <th>Type</th>
                                                            <th>Start</th>
                                                            <th>End</th>
                                                            <th>Probation End</th>
                                                            <th>Salary</th>
                                                            <th>Service Pay</th>
                                                            <th>Status</th>
                                                            <th>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {employee.contracts && employee.contracts.length > 0 ? (
                                                            employee.contracts.map((contract) => (
                                                                <tr key={contract.id}>
                                                                    <td><span className="text-capitalize">{contract.contract_type}</span></td>
                                                                    <td>{formatDate(contract.start_date)}</td>
                                                                    <td>{contract.contract_type === 'fixed_term' ? formatDate(contract.end_date) : 'Indefinite'}</td>
                                                                    <td>{contract.contract_type === 'fixed_term' ? formatDate(contract.probation_end_date) : 'N/A'}</td>
                                                                    <td>{parseFloat(contract.salary).toLocaleString()}</td>
                                                                    <td>{parseFloat(contract.service_pay).toLocaleString()}</td>
                                                                    <td>
                                                                        {!contract.is_active ? (
                                                                            <span className="badge bg-label-secondary">Terminated</span>
                                                                        ) : contract.contract_type === 'fixed_term' ? (
                                                                            <span className={`badge ${new Date(contract.end_date) < new Date() ? 'bg-label-danger' : 'bg-label-success'}`}>
                                                                                {new Date(contract.end_date) < new Date() ? 'Expired' : `${Math.ceil((new Date(contract.end_date) - new Date()) / (1000 * 60 * 60 * 24))} Days Left`}
                                                                            </span>
                                                                        ) : <span className="badge bg-label-info">Active</span>}
                                                                    </td>
                                                                    <td>
                                                                        <div className="d-flex gap-2">
                                                                            <button className="btn btn-sm btn-info-light" onClick={() => handleEditContract(contract)} data-bs-toggle="modal" data-bs-target="#addContractModal">
                                                                                <RiEditLine />
                                                                            </button>
                                                                            <button className="btn btn-sm btn-danger-light" onClick={() => handleTerminateContract(contract.id)}>
                                                                                <RiSubtractLine />
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan="8" className="text-center py-4 text-muted">No contracts found.</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-6 mb-3">
                                    <div className="card height-equal-2">
                                        <div className="card-header justify-between">
                                            <h4 className="">Personal Documents</h4>
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm"
                                                data-bs-toggle="modal"
                                                data-bs-target="#addDocumentModal"
                                                onClick={() => {
                                                    setIsEditingDocument(false);
                                                    setEditingDocumentId(null);
                                                    setDocumentForm({ document_type: '', document_file: null });
                                                }}
                                            >
                                                Add Document
                                            </button>
                                        </div>
                                        <div className="card-body pt-15">
                                            {employee.documents && employee.documents.length > 0 ? (
                                                <div className="documents-list">
                                                    {employee.documents.map((doc, index) => (
                                                        <div className="document-item mb-15 p-3 bg-light-subtle radius-10" key={doc.id || index}>
                                                            <div className="d-flex-between gap-10 mb-10">
                                                                <div className="d-flex align-items-center gap-10">
                                                                    <div className="doc-icon btn-icon btn-sm btn-primary-light fs-20">
                                                                        <RiFileTextLine />
                                                                    </div>
                                                                    <div>
                                                                        <h6 className="mb-0 fw-bold">{doc.document_type}</h6>
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex gap-2">
                                                                    {doc.document_file && (
                                                                        <a
                                                                            href={doc.document_file.startsWith('http') ? doc.document_file : API_BASE_URL + doc.document_file}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="btn btn-sm btn-success-light"
                                                                            title="Download"
                                                                        >
                                                                            <RiDownloadLine />
                                                                        </a>
                                                                    )}
                                                                    <button
                                                                        className="btn btn-sm btn-info-light"
                                                                        data-bs-toggle="modal"
                                                                        data-bs-target="#addDocumentModal"
                                                                        onClick={() => handleEditDocument(doc)}
                                                                    >
                                                                        <RiEditLine />
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm btn-danger-light"
                                                                        onClick={() => handleDeleteDocument(doc.id)}
                                                                    >
                                                                        <RiDeleteBinLine />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center py-4">
                                                    <RiFileTextLine size={40} className="text-muted mb-10 opacity-25" />
                                                    <p className="text-muted mb-0">No documents found.</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="card height-equal-2">
                                        <div className="card-header justify-between">
                                            <h4 className="">Disciplinary Records</h4>
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm"
                                                data-bs-toggle="modal"
                                                data-bs-target="#addRecordModal"
                                                onClick={() => {
                                                    setIsEditingRecord(false);
                                                    setEditingRecordId(null);
                                                    setRecordForm({ record_type: '', description: '', record_file: null });
                                                }}
                                            >
                                                Add Record
                                            </button>
                                        </div>
                                        <div className="card-body pt-15">
                                            {employee.disciplinary_records && employee.disciplinary_records.length > 0 ? (
                                                <div className="records-list">
                                                    {employee.disciplinary_records.map((record, index) => (
                                                        <div className="record-item mb-15 p-3 bg-light-subtle radius-10 border-start border-danger border-3" key={record.id || index}>
                                                            <div className="d-flex-between gap-10 mb-10">
                                                                <div className="d-flex align-items-center gap-10">
                                                                    <div className="record-icon btn-sm btn-danger-light fs-20">
                                                                        <RiAlertLine />
                                                                    </div>
                                                                    <div>
                                                                        <h6 className="mb-0 fw-bold text-capitalize">{record.record_type?.replace('_', ' ')}</h6>
                                                                        <span className="fs-12 text-muted">Created by: {record.created_by_name || 'N/A'}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex gap-2">
                                                                    {record.record_file && (
                                                                        <a
                                                                            href={record.record_file.startsWith('http') ? record.record_file : API_BASE_URL + record.record_file}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="btn btn-sm btn-success-light"
                                                                            title="Download"
                                                                        >
                                                                            <RiDownloadLine />
                                                                        </a>
                                                                    )}
                                                                    <button
                                                                        className="btn btn-sm btn-info-light"
                                                                        data-bs-toggle="modal"
                                                                        data-bs-target="#addRecordModal"
                                                                        onClick={() => handleEditRecord(record)}
                                                                    >
                                                                        <RiEditLine />
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm btn-danger-light"
                                                                        onClick={() => handleDeleteRecord(record.id)}
                                                                    >
                                                                        <RiDeleteBinLine />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            {record.description && (
                                                                <div className="mt-2">
                                                                    <p className="mb-0 fs-13 text-muted">{record.description}</p>
                                                                </div>
                                                            )}
                                                            {record.created_at && (
                                                                <div className="mt-2">
                                                                    <small className="text-muted">
                                                                        <strong>Date:</strong> {formatDate(record.created_at)}
                                                                    </small>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center py-4">
                                                    <RiAlertLine size={40} className="text-muted mb-10 opacity-25" />
                                                    <p className="text-muted mb-0">No disciplinary records found.</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>

            {/* Add Child Modal */}
            <div className="modal fade" id="addChildModal" tabIndex="-1" aria-labelledby="addChildModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content border-0 shadow-lg">
                        <div className="modal-header bg-primary text-white py-3">
                            <h5 className="modal-title fw-bold text-white" id="addChildModalLabel">
                                {isEditingChild ? 'Edit Child Details' : 'Add Employee Child'}
                            </h5>
                            <button
                                type="button"
                                className="btn-close btn-close-white"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                onClick={() => {
                                    setIsEditingChild(false);
                                    setEditingChildId(null);
                                    setChildForm({ name: '', gender: '', age: '' });
                                }}
                            ></button>
                        </div>
                        <form onSubmit={handleAddChild}>
                            <div className="modal-body p-4">
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Child Name <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        name="name"
                                        className="form-control"
                                        placeholder="Enter full name"
                                        value={childForm.name}
                                        onChange={handleChildInputChange}
                                        required
                                    />
                                </div>
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Gender <span className="text-danger">*</span></label>
                                        <select
                                            name="gender"
                                            className="form-select"
                                            value={childForm.gender}
                                            onChange={handleChildInputChange}
                                            required
                                        >
                                            <option value="">Select Gender</option>
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Age <span className="text-danger">*</span></label>
                                        <input
                                            type="number"
                                            name="age"
                                            className="form-control"
                                            placeholder="Age (must be < 18)"
                                            value={childForm.age}
                                            onChange={handleChildInputChange}
                                            max="17"
                                            min="0"
                                            required
                                        />
                                        <small className="text-muted">Must be below 18 years</small>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer bg-light border-0 py-3">
                                <button
                                    type="button"
                                    className="btn btn-label-secondary"
                                    data-bs-dismiss="modal"
                                    disabled={submittingChild}
                                    onClick={() => {
                                        setIsEditingChild(false);
                                        setEditingChildId(null);
                                        setChildForm({ name: '', gender: '', age: '' });
                                    }}
                                >Cancel</button>
                                <button type="submit" className="btn btn-primary px-4" disabled={submittingChild}>
                                    {submittingChild ? 'Saving...' : (isEditingChild ? 'Update Child' : 'Add Child')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            {/* Add Qualification Modal */}
            <div className="modal fade" id="addQualModal" tabIndex="-1" aria-labelledby="addQualModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content border-0 shadow-lg">
                        <div className="modal-header bg-primary text-white py-3">
                            <h5 className="modal-title fw-bold text-white" id="addQualModalLabel">
                                {isEditingQual ? 'Edit Qualification' : 'Add Qualification'}
                            </h5>
                            <button
                                type="button"
                                className="btn-close btn-close-white"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                onClick={() => {
                                    setIsEditingQual(false);
                                    setEditingQualId(null);
                                    setQualForm({ qualification_type: '', name: '', institution: '', year_completed: '' });
                                }}
                            ></button>
                        </div>
                        <form onSubmit={handleAddQual}>
                            <div className="modal-body p-4">
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Qualification Type <span className="text-danger">*</span></label>
                                    <select
                                        name="qualification_type"
                                        className="form-select"
                                        value={qualForm.qualification_type}
                                        onChange={handleQualInputChange}
                                        required
                                    >
                                        <option value="">Select Type</option>
                                        <option value="Degree">Degree</option>
                                        <option value="Diploma">Diploma</option>
                                        <option value="Certificate">Certificate</option>
                                        <option value="Post Graduate">Post Graduate</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Field of Study / Name <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        name="name"
                                        className="form-control"
                                        placeholder="e.g. Computer Science"
                                        value={qualForm.name}
                                        onChange={handleQualInputChange}
                                        required
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Institution <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        name="institution"
                                        className="form-control"
                                        placeholder="e.g. University of Nairobi"
                                        value={qualForm.institution}
                                        onChange={handleQualInputChange}
                                        required
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Year Completed <span className="text-danger">*</span></label>
                                    <input
                                        type="number"
                                        name="year_completed"
                                        className="form-control"
                                        placeholder="e.g. 2020"
                                        value={qualForm.year_completed}
                                        onChange={handleQualInputChange}
                                        min="1950"
                                        max={new Date().getFullYear()}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="modal-footer bg-light border-0 py-3">
                                <button
                                    type="button"
                                    className="btn btn-label-secondary"
                                    data-bs-dismiss="modal"
                                    disabled={submittingQual}
                                    onClick={() => {
                                        setIsEditingQual(false);
                                        setEditingQualId(null);
                                        setQualForm({ qualification_type: '', name: '', institution: '', year_completed: '' });
                                    }}
                                >Cancel</button>
                                <button type="submit" className="btn btn-primary px-4" disabled={submittingQual}>
                                    {submittingQual ? 'Saving...' : (isEditingQual ? 'Update Qualification' : 'Add Qualification')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            {/* Add Contract Modal */}
            <div className="modal fade" id="addContractModal" tabIndex="-1" aria-labelledby="addContractModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content border-0 shadow-lg">
                        <div className="modal-header bg-primary text-white py-3">
                            <h5 className="modal-title fw-bold text-white" id="addContractModalLabel">
                                {isEditingContract ? 'Edit Contract' : 'Add Contract'}
                            </h5>
                            <button
                                type="button"
                                className="btn-close btn-close-white"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                onClick={() => {
                                    setIsEditingContract(false);
                                    setEditingContractId(null);
                                    setContractForm({ contract_type: '', start_date: '', end_date: '', salary: '', probation_period: 0, probation_end_date: '' });
                                }}
                            ></button>
                        </div>
                        <form onSubmit={handleAddContract}>
                            <div className="modal-body p-4">
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Contract Type <span className="text-danger">*</span></label>
                                        <select
                                            name="contract_type"
                                            className="form-select"
                                            value={contractForm.contract_type}
                                            onChange={handleContractInputChange}
                                            required
                                        >
                                            <option value="">Select Type</option>
                                            <option value="fixed_term">Fixed Term</option>
                                            <option value="open">Full-Time</option>
                                            <option value="student">Student</option>
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Start Date <span className="text-danger">*</span></label>
                                        <input
                                            type="date"
                                            name="start_date"
                                            className="form-control"
                                            value={contractForm.start_date}
                                            onChange={handleContractInputChange}
                                            required
                                        />
                                    </div>
                                </div>

                                {contractForm.contract_type === 'fixed_term' && (
                                    <div className="row g-3 mb-3">
                                        <div className="col-md-4">
                                            <label className="form-label fw-bold">End Date <span className="text-danger">*</span></label>
                                            <input
                                                type="date"
                                                name="end_date"
                                                className="form-control"
                                                value={contractForm.end_date}
                                                onChange={handleContractInputChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label fw-bold">Probation Period</label>
                                            <select
                                                name="probation_period"
                                                className="form-select"
                                                value={contractForm.probation_period}
                                                onChange={handleContractInputChange}
                                            >
                                                <option value="0">No Probation</option>
                                                <option value="1">1 Month</option>
                                                <option value="2">2 Months</option>
                                                <option value="3">3 Months</option>
                                            </select>
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label fw-bold">Probation End Date</label>
                                            <input
                                                type="date"
                                                name="probation_end_date"
                                                className="form-control bg-light"
                                                value={contractForm.probation_end_date}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Salary (USD) <span className="text-danger">*</span></label>
                                        <input
                                            type="number"
                                            name="salary"
                                            className="form-control"
                                            placeholder="Enter monthly salary"
                                            value={contractForm.salary}
                                            onChange={handleContractInputChange}
                                            required
                                        />
                                    </div>

                                </div>
                            </div>
                            <div className="modal-footer bg-light border-0 py-3">
                                <button
                                    type="button"
                                    className="btn btn-label-secondary"
                                    data-bs-dismiss="modal"
                                    disabled={submittingContract}
                                    onClick={() => {
                                        setIsEditingContract(false);
                                        setEditingContractId(null);
                                        setContractForm({ contract_type: '', start_date: '', end_date: '', salary: '', probation_period: 0, probation_end_date: '' });
                                    }}
                                >Cancel</button>
                                <button type="submit" className="btn btn-primary px-4" disabled={submittingContract}>
                                    {submittingContract ? 'Saving...' : (isEditingContract ? 'Update Contract' : 'Add Contract')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Add Document Modal */}
            <div className="modal fade" id="addDocumentModal" tabIndex="-1" aria-labelledby="addDocumentModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content border-0 shadow-lg">
                        <div className="modal-header bg-primary text-white py-3">
                            <h5 className="modal-title fw-bold text-white" id="addDocumentModalLabel">
                                {isEditingDocument ? 'Edit Document' : 'Add Employee Document'}
                            </h5>
                            <button
                                type="button"
                                className="btn-close btn-close-white"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                onClick={() => {
                                    setIsEditingDocument(false);
                                    setEditingDocumentId(null);
                                    setDocumentForm({ document_type: '', document_file: null });
                                }}
                            ></button>
                        </div>
                        <form onSubmit={handleAddDocument}>
                            <div className="modal-body p-4">
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Document Type <span className="text-danger">*</span></label>
                                        <select
                                            name="document_type"
                                            className="form-select"
                                            value={documentForm.document_type}
                                            onChange={handleDocumentInputChange}
                                            required
                                        >
                                            <option value="">Select Type</option>
                                            <option value="passport">Passport</option>
                                            <option value="id_card">ID Card</option>
                                            <option value="drivers_license">Driver's License</option>
                                            <option value="birth_certificate">Birth Certificate</option>
                                            <option value="degree">Degree/Certificate</option>
                                            <option value="contract">Contract</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Upload Document {!isEditingDocument && <span className="text-danger">*</span>}</label>
                                        <input
                                            type="file"
                                            name="document_file"
                                            className="form-control"
                                            onChange={handleDocumentInputChange}
                                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                            required={!isEditingDocument}
                                        />
                                        <small className="text-muted">Accepted formats: PDF, DOC, DOCX, JPG, PNG (Max 10MB)</small>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer bg-light border-0 py-3">
                                <button
                                    type="button"
                                    className="btn btn-label-secondary"
                                    data-bs-dismiss="modal"
                                    disabled={submittingDocument}
                                    onClick={() => {
                                        setIsEditingDocument(false);
                                        setEditingDocumentId(null);
                                        setDocumentForm({ document_type: '', document_file: null });
                                    }}
                                >Cancel</button>
                                <button type="submit" className="btn btn-primary px-4" disabled={submittingDocument}>
                                    {submittingDocument ? 'Saving...' : (isEditingDocument ? 'Update Document' : 'Add Document')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Add Disciplinary Record Modal */}
            <div className="modal fade" id="addRecordModal" tabIndex="-1" aria-labelledby="addRecordModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content border-0 shadow-lg">
                        <div className="modal-header bg-danger text-white py-3">
                            <h5 className="modal-title fw-bold text-white" id="addRecordModalLabel">
                                {isEditingRecord ? 'Edit Disciplinary Record' : 'Add Disciplinary Record'}
                            </h5>
                            <button
                                type="button"
                                className="btn-close btn-close-white"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                onClick={() => {
                                    setIsEditingRecord(false);
                                    setEditingRecordId(null);
                                    setRecordForm({ record_type: '', description: '', record_file: null });
                                }}
                            ></button>
                        </div>
                        <form onSubmit={handleAddRecord}>
                            <div className="modal-body p-4">
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Record Type <span className="text-danger">*</span></label>
                                    <select
                                        name="record_type"
                                        className="form-select"
                                        value={recordForm.record_type}
                                        onChange={handleRecordInputChange}
                                        required
                                    >
                                        <option value="">Select Type</option>
                                        <option value="verbal_warning">Verbal Warning</option>
                                        <option value="written_warning">Written Warning</option>
                                        <option value="final_warning">Final Warning</option>
                                        <option value="suspension">Suspension</option>
                                        <option value="termination">Termination</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Description <span className="text-danger">*</span></label>
                                    <textarea
                                        name="description"
                                        className="form-control"
                                        rows="4"
                                        placeholder="Provide details about the disciplinary action..."
                                        value={recordForm.description}
                                        onChange={handleRecordInputChange}
                                        required
                                    ></textarea>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Upload Supporting Document</label>
                                    <input
                                        type="file"
                                        name="record_file"
                                        className="form-control"
                                        onChange={handleRecordInputChange}
                                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                    />
                                    <small className="text-muted">Optional: Attach any supporting documents (PDF, DOC, DOCX, JPG, PNG)</small>
                                </div>
                            </div>
                            <div className="modal-footer bg-light border-0 py-3">
                                <button
                                    type="button"
                                    className="btn btn-label-secondary"
                                    data-bs-dismiss="modal"
                                    disabled={submittingRecord}
                                    onClick={() => {
                                        setIsEditingRecord(false);
                                        setEditingRecordId(null);
                                        setRecordForm({ record_type: '', description: '', record_file: null });
                                    }}
                                >Cancel</button>
                                <button type="submit" className="btn btn-danger px-4" disabled={submittingRecord}>
                                    {submittingRecord ? 'Saving...' : (isEditingRecord ? 'Update Record' : 'Add Record')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmployeeDetails;
